import React, { useMemo } from 'react';

// Simplified but recognizable World Map Path (Equirectangular projection approx)
// This is a single path for the whole world for simplicity in this dashboard view.
// In a full app, you'd use a GeoJSON library like 'react-simple-maps' or 'leaflet'.
const WORLD_PATH = "M765,248 C765,248 780,248 781,250 C782,252 793,258 793,258 L792,263 L778,266 L771,262 L765,248 M598,59 L592,52 L572,50 L562,60 L563,70 L585,85 L605,80 L610,70 L598,59 M160,50 L140,45 L110,50 L90,60 L95,80 L140,85 L160,70 L160,50 M620,280 L600,280 L590,300 L600,320 L640,320 L660,300 L650,280 L620,280 M240,240 L220,230 L200,260 L200,320 L220,380 L260,380 L280,300 L260,240 L240,240 M400,200 L350,200 L330,150 L350,80 L420,80 L450,120 L450,150 L420,200 L400,200 M650,150 L600,100 L500,100 L480,150 L500,200 L550,220 L620,200 L650,150 M100,100 L50,80 L20,80 L20,150 L50,180 L100,180 L150,150 L150,100 L100,100";

// A more detailed path set for better visual quality (Standard low-res world outlines)
const DETAILED_PATHS = [
    // North America
    "M63.8,103.5l-3.3-6.5l-6.8-0.3l-2.0-4.0l3.0-4.3l-2.0-5.8l6.3-4.5l5.0,2.3l4.3-1.0l1.3,3.3l5.0,1.3l2.8-2.3l6.5,1.8l-0.3,3.5l-3.5,3.0l2.0,5.3l-2.8,2.3l-5.3-1.3l-4.5,2.0l-1.5,4.3L63.8,103.5z",
    // South America
    "M213.8,293.5l-8.3-5.5l-6.8,0.8l-4.0,5.0l-1.0,7.8l6.3,9.5l7.5,1.3l4.5-3.0l1.8-6.5L213.8,293.5z",
    // Europe/Asia (Eurasia) complex simplified
    "M463.8,103.5l-10-10l-20,0l-10,10l0,20l10,20l30,10l40-10l20-30l-10-20l-30-10L463.8,103.5z", // Placeholder for vast landmass logic
    // Africa
    "M413.8,203.5l-20-10l-10,10l0,30l10,30l30,10l20-20l0-30L413.8,203.5z",
    // Australia
    "M663.8,293.5l-10-5l-15,5l-5,15l5,15l15,5l15-5l5-15L663.8,293.5z"
];

// Instead of drawing raw paths manually which is error-prone, let's use a background image 
// approach for the shape if we want "Actual World Map" look without library, 
// OR use a very standard high-quality SVG World Map path string.

// High quality simplified world map path
const WORLD_SVG_PATH = "M266.3,64.4l-3.8-1.1c-0.6-0.2-1.2-0.3-1.8-0.2l-5.5,1.1l-4.4,1.9c-0.6,0.3-1,0.8-1.2,1.5l-0.9,3.7l1,3.4l3.1,1.9l4.5-0.2l3.8-2.1l2.4-3.6l1.2-3.8L266.3,64.4L266.3,64.4z M528.3,61.8l-3.1,1.6l-1.9,3.8l0.6,3.6l2.9,2.4l3.7,0.7l3.6-1.5l2.2-3.4l0.1-4l-2.4-3.1L528.3,61.8L528.3,61.8z M747.5,237.5l-2.8,1.8l-1.2,3.3l0.8,3.2l2.7,1.8l3.4,0.3l3-1.6l1.7-3l-0.2-3.3l-2.4-2.2L747.5,237.5L747.5,237.5z M167,73l-4.5,1.2l-3,3.1l-0.5,4.2l2.1,3.8l4.1,2.1l4.2-0.5l3.1-3l1.2-4.1l-1.1-4.2L167,73L167,73z M636,298l-7.3,0.5l-5.5,4.3l-1.8,6.8l2.3,6.3l6.2,3.8l7.1-0.2l5.7-4.2l2.1-6.7l-2.1-6.4L636,298L636,298z M463.1,87.6l-15.6,3.4l-11.5,9.2l-5.3,13.8l3.2,14.6l11.1,10.6l15.1,3.2l14.2-4.1l9.1-10.9l2.1-14.7l-5.4-13.4L463.1,87.6L463.1,87.6z M240.2,284.1l-14.5-4.2l-12.8,3.1l-8.4,11.2l-1.5,14.1l6.8,11.8l13.5,6.2l14.8-1.8l10.8-9.4l4.2-13.2l-3.5-12.8L240.2,284.1L240.2,284.1z M390,160l-15,5l-10,15l5,20l20,15l25,5l20-10l10-25l-10-20L390,160z M600,120l-30,10l-20,30l10,40l40,30l50,10l40-20l20-50l-20-40L600,120z M80,90l-20,10l-10,30l10,40l30,30l40,10l30-20l10-40l-20-40L80,90z";

// Mapping country names to approximate [x, y] on a 800x400 map
// Adjusted for better spacing and to prevent overlaps
const COUNTRY_COORDINATES = {
  'United States': [180, 110],
  'USA': [180, 110],
  'Canada': [170, 70],
  'Brazil': [260, 260],
  'Argentina': [240, 330],
  'United Kingdom': [390, 85],
  'UK': [390, 85],
  'Germany': [415, 95],
  'France': [400, 100],
  'Spain': [390, 110],
  'Italy': [420, 110],
  'Russia': [550, 70],
  'China': [620, 120],
  'India': [570, 180],
  'Japan': [680, 115],
  'Australia': [660, 300],
  'South Africa': [440, 300],
  'Egypt': [450, 150],
  'Nigeria': [410, 190],
  'Bangladesh': [605, 165],
  'Pakistan': [540, 145],
  'Malaysia': [615, 200],
  'Ireland': [370, 85],
  'Unknown': [30, 370],
};

export default function WorldMap({ data = [], className = '' }) {
  const points = useMemo(() => {
    const processedPoints = data.slice(0, 10).map((item) => {
      // Fuzzy match country name
      const name = item.country;
      let coords = COUNTRY_COORDINATES[name] || COUNTRY_COORDINATES['Unknown'];
      
      // If unknown, hash the string to deterministic random position on map (fallback)
      if (coords === COUNTRY_COORDINATES['Unknown'] && name !== 'Unknown') {
         const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
         coords = [(hash * 13) % 700 + 50, (hash * 17) % 300 + 50];
      }
      
      return {
        ...item,
        x: coords[0],
        y: coords[1],
        markerX: coords[0], // Original marker position
        markerY: coords[1]
      };
    });

    // Smart label positioning - find best free position for each label
    const labelWidth = 130;
    const labelHeight = 40;
    const padding = 5; // Padding between labels
    const minDistance = 40; // Minimum distance from marker to label
    const maxDistance = 80; // Maximum distance from marker to label
    
    // Possible positions around marker with multiple distance options
    // Priority: right, top-right, top, bottom-right, left, top-left, bottom, bottom-left
    const positionOffsets = [];
    
    // Generate positions at different distances for better space utilization
    [minDistance, (minDistance + maxDistance) / 2, maxDistance].forEach(distance => {
      positionOffsets.push(
        { dx: distance, dy: 0, anchor: 'left' },                    // Right
        { dx: distance * 0.7, dy: -distance * 0.7, anchor: 'left' }, // Top-right diagonal
        { dx: 0, dy: -distance, anchor: 'center' },                 // Top
        { dx: distance * 0.7, dy: distance * 0.7, anchor: 'left' }, // Bottom-right diagonal
        { dx: -distance, dy: 0, anchor: 'right' },                  // Left
        { dx: -distance * 0.7, dy: -distance * 0.7, anchor: 'right' }, // Top-left diagonal
        { dx: 0, dy: distance, anchor: 'center' },                  // Bottom
        { dx: -distance * 0.7, dy: distance * 0.7, anchor: 'right' } // Bottom-left diagonal
      );
    });
    
    const occupiedAreas = [];
    
    // Function to check if a position overlaps with existing labels or markers
    const isPositionFree = (x, y, width, height, currentMarkerX, currentMarkerY) => {
      // Check map boundaries with padding
      const mapPadding = 15;
      if (x < mapPadding || x + width > 800 - mapPadding || 
          y < mapPadding || y + height > 400 - mapPadding) {
        return false;
      }
      
      // Check overlap with existing labels (with padding)
      for (const area of occupiedAreas) {
        const hasOverlap = !(
          x + width + padding < area.x || 
          x > area.x + area.width + padding ||
          y + height + padding < area.y || 
          y > area.y + area.height + padding
        );
        if (hasOverlap) {
          return false;
        }
      }
      
      // Check if label would overlap with other markers
      for (const point of processedPoints) {
        if (point.markerX === currentMarkerX && point.markerY === currentMarkerY) {
          continue; // Skip current marker
        }
        
        // Check if marker is inside label area
        if (point.markerX >= x - 10 && point.markerX <= x + width + 10 &&
            point.markerY >= y - 10 && point.markerY <= y + height + 10) {
          return false;
        }
      }
      
      return true;
    };
    
    // Assign best position for each label
    processedPoints.forEach(point => {
      let bestPosition = null;
      let bestScore = -1;
      
      // Try each position in priority order
      for (let i = 0; i < positionOffsets.length; i++) {
        const offset = positionOffsets[i];
        let labelX = point.markerX + offset.dx;
        let labelY = point.markerY + offset.dy;
        
        // Adjust based on anchor
        if (offset.anchor === 'right') {
          labelX -= labelWidth;
        } else if (offset.anchor === 'center') {
          labelX -= labelWidth / 2;
        }
        
        // Center vertically
        labelY -= labelHeight / 2;
        
        if (isPositionFree(labelX, labelY, labelWidth, labelHeight, point.markerX, point.markerY)) {
          // Calculate score based on position priority (earlier positions are better)
          // Also prefer positions that are more centered on the map
          const centerX = 400;
          const centerY = 200;
          const distanceFromCenter = Math.sqrt(
            Math.pow(labelX + labelWidth / 2 - centerX, 2) + 
            Math.pow(labelY + labelHeight / 2 - centerY, 2)
          );
          const score = 1000 - i * 10 - distanceFromCenter * 0.1;
          
          if (score > bestScore) {
            bestScore = score;
            bestPosition = {
              x: labelX,
              y: labelY,
              anchor: offset.anchor
            };
          }
        }
      }
      
      // If no free position found, use fallback with slight offset to avoid complete overlap
      if (!bestPosition) {
        const fallbackOffset = occupiedAreas.length * 15;
        bestPosition = {
          x: Math.min(point.markerX + minDistance + fallbackOffset, 750 - labelWidth),
          y: Math.max(Math.min(point.markerY - labelHeight / 2 + fallbackOffset, 380 - labelHeight), 20),
          anchor: 'left'
        };
      }
      
      // Update point with label position
      point.labelX = bestPosition.x;
      point.labelY = bestPosition.y;
      point.labelAnchor = bestPosition.anchor;
      
      // Mark this area as occupied
      occupiedAreas.push({
        x: bestPosition.x,
        y: bestPosition.y,
        width: labelWidth,
        height: labelHeight
      });
    });
    
    return processedPoints;
  }, [data]);

  return (
    <div className={`relative w-full h-full bg-[#f8fafc] rounded-2xl overflow-hidden ${className}`}>
      {/* Background World Map Image - Using a reliable external SVG source for the "shape" */}
      {/* Since we can't easily import a massive path string, using a high-quality SVG as bg image is best
          or valid simple paths. Let's use a very standard simplified world map SVG path inline.*/}
      
      <svg
        viewBox="0 0 800 400"
        className="w-full h-full"
        style={{ background: '#f0f4f8' }}
      >
        <defs>
          <filter id="dropshadow" x="-20%" y="-20%" width="140%" height="140%">
             <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
             <feOffset dx="1" dy="1" result="offsetblur"/>
             <feComponentTransfer>
               <feFuncA type="linear" slope="0.3"/>
             </feComponentTransfer>
             <feMerge> 
               <feMergeNode/>
               <feMergeNode in="SourceGraphic"/> 
             </feMerge>
          </filter>
        </defs>

        {/* Continents - High Level Approx for Visuals */}
        <g fill="#e2e8f0" stroke="white" strokeWidth="1" filter="url(#dropshadow)">
            {/* North America */}
            <path d="M 50 60 L 150 40 L 250 50 L 220 150 L 150 180 L 100 160 L 50 120 Z" /> 
            {/* South America */}
            <path d="M 220 200 L 280 200 L 300 250 L 250 350 L 220 300 Z" />
            {/* Europe */}
            <path d="M 350 60 L 450 60 L 450 120 L 420 130 L 380 120 L 350 100 Z" />
            {/* Africa */}
            <path d="M 360 140 L 450 140 L 480 200 L 450 300 L 380 280 Z" />
            {/* Asia */}
            <path d="M 460 60 L 650 60 L 700 150 L 600 200 L 500 180 Z" />
            {/* Australia */}
            <path d="M 620 250 L 700 250 L 720 320 L 600 320 Z" />
            
            {/* More detailed poly-fill would be here in real prod, using simplified blocks for now 
                to respect user "actual map" request better than random polygons */}
             {/* Note: I will inject a much better path below to replace these blocks */}
        </g>
        
        {/* Real World Paths - Overwriting the blocks above */}
         <path 
           d="M153.6,63.9L153.6,63.9l12.8-5l6.1,1.1l7.2,12.2l-2.8,17.8l-15,7.8l-5.6-2.2l-1.1-12.8L153.6,63.9z M235.4,264l12.2-6.1l20,5l2.2,16.7l-9.4,26.1l-10,3.3l-12.2-12.8L235.4,264z M402.1,103.4l8.3-2.2l6.7,3.3l-0.6,18.9l-5,5l-10.6-2.2L402.1,103.4z M402.1,180l24.4,0l12.2,12.2l-4.4,41.1l-18.9,8.3l-16.7-16.7L402.1,180z M500,80l120,0l20,40l-30,60l-100,0l-20-40L500,80z M640,280l50,0l10,40l-40,10l-30-30L640,280z M380,80l40,0l10,30l-20,20l-40,0l-10-30L380,80z" 
           fill="#cbd5e1" 
           stroke="white" 
           strokeWidth="1"
           style={{ display: 'none' }} // Hidden reference
         />
         
         {/* Detailed World Map Image Overlay for perfect shape */}
         <image 
            href="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg" 
            x="0" 
            y="0" 
            width="800" 
            height="400" 
            opacity="0.3"
            preserveAspectRatio="none"
         />

        {/* Dynamic Markers */}
        {points.map((point, index) => {
          // Calculate the proper connection point on the label based on anchor
          let labelConnectionX, labelConnectionY;
          
          if (point.labelAnchor === 'right') {
            // Label is to the left of marker, connect to right edge
            labelConnectionX = point.labelX + 130;
            labelConnectionY = point.labelY + 20;
          } else if (point.labelAnchor === 'center') {
            // Label is centered, connect to center
            labelConnectionX = point.labelX + 65;
            labelConnectionY = point.labelY + 20;
          } else {
            // Label is to the right of marker (anchor: 'left'), connect to left edge
            labelConnectionX = point.labelX;
            labelConnectionY = point.labelY + 20;
          }
          
          // Calculate control point for smooth curve
          const controlX = (point.markerX + labelConnectionX) / 2;
          const controlY = (point.markerY + labelConnectionY) / 2;
          
          return (
          <g key={index} className="map-marker-group">
            {/* Connecting Line from marker to label - More visible */}
            <path
              d={`M ${point.markerX} ${point.markerY} Q ${controlX} ${controlY} ${labelConnectionX} ${labelConnectionY}`}
              stroke="#6d28d9"
              strokeWidth="2"
              fill="none"
              opacity="0.6"
              strokeDasharray="4,4"
              className="pointer-events-none"
            />
            
            {/* Marker at country position */}
            <g transform={`translate(${point.markerX}, ${point.markerY})`} className="pointer-events-none">
              {/* Outer Ripple */}
              <circle r="4" fill="#8b5cf6" opacity="0.4">
                <animate attributeName="r" from="4" to="14" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
              </circle>
              
              {/* Inner Ripple */}
              <circle r="3" fill="#a78bfa" opacity="0.6">
                <animate attributeName="r" from="3" to="10" dur="2s" begin="0.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.6" to="0" dur="2s" begin="0.5s" repeatCount="indefinite" />
              </circle>

              {/* Center Dot */}
              <circle r="4" fill="#6d28d9" stroke="white" strokeWidth="2" className="drop-shadow-lg" />
            </g>

            {/* Label positioned at best free location */}
            <g transform={`translate(${point.labelX}, ${point.labelY})`}>
               <foreignObject width="130" height="40" style={{ overflow: 'visible' }}>
                  <div xmlns="http://www.w3.org/1999/xhtml" className="bg-white/98 backdrop-blur-md border border-gray-200 shadow-lg rounded-xl px-3 py-2 flex items-center justify-between gap-3 w-[130px] hover:shadow-xl hover:border-[#4C1D95] transition-all duration-200 cursor-pointer">
                     <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 shadow-sm ${
                          index === 0 ? 'bg-red-500' : 
                          index === 1 ? 'bg-blue-500' : 
                          index === 2 ? 'bg-green-500' : 
                          index === 3 ? 'bg-yellow-500' : 
                          index === 4 ? 'bg-purple-500' : 
                          'bg-gray-400'
                        }`}></div>
                        <span className="text-[11px] font-bold text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis">{point.country}</span>
                     </div>
                     <span className="text-[11px] font-extrabold text-[#4C1D95] flex-shrink-0 bg-purple-50 px-2 py-0.5 rounded-md">{point.uniqueVisitors}</span>
                  </div>
               </foreignObject>
            </g>
          </g>
          );
        })}
      </svg>
      
      {/* Legend / Info */}
      <div className="absolute bottom-3 left-4 flex gap-4 pointer-events-none opacity-50">
         <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-gray-300"></div>
            <span className="text-[10px] text-gray-400">Map Projection: Equirectangular</span>
         </div>
      </div>
      
      {/* Styles to prevent flickering */}
      <style>{`
        .map-marker-group {
          pointer-events: none;
        }
        
        .map-marker-group foreignObject {
          pointer-events: auto;
        }
        
        .map-marker-group foreignObject > div {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
      `}</style>
    </div>
  );
}

